/**
 * Stawki narzędzia „Sprawdź ofertę instalatora” (`/sprawdz-oferte`).
 *
 * ═══ TO JEST PLIK DO RĘCZNEJ EDYCJI ═══
 * Wszystkie liczby, które nie pochodzą z katalogu, stoją tutaj i nigdzie indziej.
 * Każda ma źródło i datę, bo raport pokazuje je klientowi jako założenia.
 * Plan i decyzje: `VV/PLAN_SPRAWDZ_OFERTE.md` §0.
 *
 * Wszystkie kwoty są NETTO. Ceny sklepów w katalogu są brutto z 23% i przeliczamy
 * je na netto w `lib/szacunekOferty.js`.
 */

/** Stawka VAT, w której podane są ceny sklepów w katalogu. */
export const VAT_SKLEPOW = 23

/** Stawki VAT do wyboru przy cenie oferty. 8% to montaż na domu do 300 m². */
export const STAWKI_VAT_OFERTY = [
  { v: 8, l: '8% (dom do 300 m²)' },
  { v: 23, l: '23% (firma)' },
]

/**
 * Konstrukcja i montaż to nasze stawki, a nie ceny z rynku, więc pokazujemy je
 * jako widełki. Decyzja właściciela 2026-09-27: ±15%.
 */
export const ROZRZUT_KONSTRUKCJI = 0.15
export const ROZRZUT_MONTAZU = 0.15

/**
 * Zasilanie awaryjne zaznaczone w ofercie: dodatkowe prace techniczne przy montażu.
 * Stawka właściciela, 2026-09-27.
 */
export const ZASILANIE_AWARYJNE_NETTO = 1500

/**
 * Osobny moduł zasilania awaryjnego, gdy falownik nie ma go wbudowanego
 * (Huawei, SolarEdge, część FoxESS). Mediana 24 cen ze sklepów, 3 040 zł brutto,
 * `VV/dane_badawcze/koszty_dodatkow.csv`, 2026-09-27.
 */
export const MODUL_ZASILANIA_AWARYJNEGO_NETTO = 2472

/** Optymalizator mocy za sztukę. Mediana 21 cen, 226 zł brutto, to samo źródło. */
export const OPTYMALIZATOR_NETTO_SZT = 184

/**
 * „Obsługa, gwarancja i koszty firmy”: procent od ROBOCIZNY (konstrukcja, montaż, zasilanie
 * awaryjne), nie od sprzętu. 20–40%, decyzja właściciela 2026-10-07 po kalibracji na 18 ofertach
 * z grup i OLX (`VV/dane_badawcze/kalibracja_narzutu_2026-10-06.md`): liczony od całości dawał
 * 11 ofert z 18 poniżej zakresu i typową ofertę 13–17% pod środkiem, bo ceny sprzętu to ceny
 * sklepów z ich marżą, a firma kupuje taniej. Od robocizny: 10 z 18 w zakresie, mediana przy środku.
 * Historia: 30–50% od całości do 2026-09-28, potem 20–40% od całości.
 */
export const KOSZTY_FIRMY = { od: 0.20, do: 0.40 }

/**
 * Narzut na obsługę sprzedaży (prowizja handlowca, marketing), NETTO, doliczany do pozycji
 * „Obsługa, gwarancja i koszty firmy”. Decyzja właściciela 2026-10-14: kalibracja na ofertach małych
 * firm z grup i OLX nie obejmowała prowizji handlowców dużych firm, przez co nasz zakres był najniższy
 * w wynikach Google. Punkty krzywej: do 5 kWp 1 000–3 000 zł, 10 kWp 2 000–6 000 zł, od 15 kWp
 * 5 000–10 000 zł; pomiędzy liniowo, żeby nie było skoku ceny na progu (np. 11–12 kWp za kWp drożej
 * niż 10 kWp).
 * ZASADA: tych kwot NIE pokazujemy publicznie (raport, artykuły, opisy). W rozbiciu ceny są
 * częścią „kosztów firmy”, bez osobnej pozycji i bez kwot.
 */
export const OBSLUGA_SPRZEDAZY = [
  { kwp: 5, od: 1000, do: 3000 },
  { kwp: 10, od: 2000, do: 6000 },
  { kwp: 15, od: 5000, do: 10000 },
]

/** Narzut na obsługę sprzedaży dla mocy instalacji (liniowo między punktami); null bez znanej mocy. */
export const obslugaSprzedazy = (kwp) => {
  if (!(kwp > 0)) return null
  const pkt = OBSLUGA_SPRZEDAZY
  if (kwp <= pkt[0].kwp) return { od: pkt[0].od, do: pkt[0].do }
  const ost = pkt[pkt.length - 1]
  if (kwp >= ost.kwp) return { od: ost.od, do: ost.do }
  const i = pkt.findIndex((x) => kwp < x.kwp)
  const a = pkt[i - 1]
  const b = pkt[i]
  const u = (kwp - a.kwp) / (b.kwp - a.kwp)
  return { od: a.od + (b.od - a.od) * u, do: a.do + (b.do - a.do) * u }
}

/** Pozycje szacunku, od których liczymy koszty firmy. */
export const POZYCJE_ROBOCIZNY = ['konstrukcja', 'montaz', 'zasilanie', 'montaz_magazynu']

/**
 * Montaż magazynu przy rozbudowie istniejącej instalacji (tryb „Rozbudowuję instalację o magazyn”),
 * NETTO, według łącznej pojemności: do 10 kWh 1 000 zł, powyżej 10 kWh 1 500 zł, powyżej 30 kWh
 * 2 000 zł. Stawki właściciela, 2026-10-07. Obejmuje też ewentualną wymianę falownika na hybrydowy.
 */
export const MONTAZ_MAGAZYNU_NETTO = [
  { doKwh: 10, zl: 1000 },
  { doKwh: 30, zl: 1500 },
  { doKwh: Infinity, zl: 2000 },
]
export const montazMagazynu = (kwh) => (kwh > 0 ? MONTAZ_MAGAZYNU_NETTO.find((s) => kwh <= s.doKwh).zl : null)

/**
 * Magazyn bez ceny w katalogu (nieznany model albo marka spoza katalogu): szacunek
 * z pojemności, w zł za kWh BRUTTO jak ceny sklepów. Próbka z katalogu 2026-10-01:
 * 272 magazyny 4–20 kWh bez zestawów z falownikiem, najniższa cena oferty na kWh:
 * mediana 914 zł, połowa modeli między 720 a 1181 zł (popularne, od 3 ofert: 660–1075 zł).
 * Szeroki zakres, bo to szacunek bez modelu (prośba właściciela: lepiej niż „brak ceny”).
 */
export const MAGAZYN_ZL_ZA_KWH_BRUTTO = { od: 700, do: 1150 }

/**
 * Moduły i falownik bez ceny w katalogu: szacunek z mocy, BRUTTO jak ceny sklepów
 * (prośba właściciela 2026-10-01). Próbka katalogu z tego dnia, najniższa cena oferty:
 * moduły 380–650 W: mediana 0,74 zł/W, połowa modeli 0,68–0,82 zł/W (416 modeli);
 * falowniki 3–20 kW hybrydowe: mediana 679 zł/kW, połowa 518–926 zł/kW (385 modeli);
 * sieciowe: mediana 487 zł/kW, połowa 280–754 zł/kW (368 modeli). Oferta z magazynem ma
 * w praktyce falownik hybrydowy; bez magazynu bierzemy zakres obejmujący oba typy.
 */
export const MODUL_ZL_ZA_W_BRUTTO = { od: 0.68, do: 0.82 }
export const FALOWNIK_ZL_ZA_KW_BRUTTO = {
  hybrydowy: { od: 520, do: 930 },
  dowolny: { od: 300, do: 900 },
}

/**
 * Trzy rodzaje dachu zamiast dziewięciu połaci z `lib/konstrukcja.js`
 * (decyzja 2026-09-27). Stawka grupy to średnia jej połaci.
 */
export const DACHY = [
  { key: 'blacha', label: 'Dach z blachy', opis: 'trapezowa, blachodachówka',
    polacie: ['blacha_trapezowa', 'blachodachowka'] },
  { key: 'dachowka', label: 'Dach z dachówki', opis: 'ceramiczna, karpiówka',
    polacie: ['dachowka_ceramiczna', 'dachowka_karpiowka'] },
  { key: 'grunt', label: 'Grunt', opis: 'konstrukcja wbijana', polacie: ['grunt'] },
]
